import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { attribution } from "@/lib/attribution";
import { api, formatApiError } from "@/lib/api";
import { compressImage, readableSize } from "@/lib/compressImage";
import { RX_COPY, RX_LANGUAGES } from "@/lib/rxCopy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, FileText, Loader2, Lock, Phone, Upload } from "lucide-react";

const MAX_BYTES = 5 * 1024 * 1024;
const EMPTY = { name: "", phone: "", address: "", note: "", consent: false, company: "" };

/**
 * Public prescription upload. Built for a phone on a weak connection: one column,
 * large targets, the photo compressed in the browser, and a referral code carried
 * silently from the QR poster to the saved record.
 */
export default function UploadPrescription() {
  const [params] = useSearchParams();
  const src = (params.get("src") || "").trim();
  const ref = (params.get("ref") || "").trim().toUpperCase();
  const [lang, setLang] = useState("en");
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [known, setKnown] = useState(false);
  const picker = useRef(null);
  const t = useMemo(() => RX_COPY[lang], [lang]);

  // Count the scan against the poster it came from; unknown codes are ignored.
  useEffect(() => {
    const code = ref || src;
    if (code) api.post("/referral-visits", { src: code }).catch(() => {});
  }, [src, ref]);

  // A returning patient types their number and the rest is already there. Only empty
  // fields are filled, so nothing the patient has typed is ever overwritten.
  useEffect(() => {
    const digits = form.phone.replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) return;
    const timer = setTimeout(() => {
      api.get("/prescription-uploads/known", { params: { phone: digits } })
        .then(({ data }) => {
          if (!data.known) return;
          setForm((f) => ({
            ...f,
            name: f.name.trim() ? f.name : data.name || "",
            address: f.address.trim() ? f.address : data.address || "",
          }));
          setKnown(Boolean(data.name || data.address));
        })
        .catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [form.phone]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const pick = async (e) => {
    const chosen = e.target.files?.[0];
    if (!chosen) return;
    const ready = await compressImage(chosen);
    if (ready.size > MAX_BYTES) {
      toast.error(t.errBig);
      return;
    }
    setFile(ready);
  };

  const submit = async (e) => {
    e.preventDefault();
    const digits = form.phone.replace(/\D/g, "").slice(-10);
    if (form.name.trim().length < 2) return toast.error(t.errName);
    if (digits.length !== 10 || !"6789".includes(digits[0])) return toast.error(t.errPhone);
    if (form.address.trim().length < 10) return toast.error(t.errAddress);
    if (!file) return toast.error(t.errFile);
    if (!form.consent) return toast.error(t.errConsent);

    const payload = new FormData();
    payload.append("name", form.name.trim());
    payload.append("phone", digits);
    payload.append("address", form.address.trim());
    payload.append("note", form.note.trim());
    payload.append("consent", "true");
    payload.append("src", src || attribution().src);
    payload.append("ref", ref || attribution().ref);
    payload.append("company", form.company);
    payload.append("file", file);

    setBusy(true);
    try {
      await api.post("/prescription-uploads", payload, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(`+91 ${digits}`);
      setForm(EMPTY);
      setFile(null);
      setKnown(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const langToggle = (
    <div className="inline-flex rounded-full border border-border p-1 bg-background" data-testid="rx-lang-toggle">
      {RX_LANGUAGES.map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLang(code)}
          data-testid={`rx-lang-${code}`}
          className={`px-4 h-8 rounded-full text-sm transition-colors ${
            lang === code ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary"
          }`}
        >
          {RX_COPY[code].label}
        </button>
      ))}
    </div>
  );

  if (done) {
    return (
      <main className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="rx-thanks">
        <span className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={26} />
        </span>
        <h1 className="font-display text-4xl mb-3">{t.thanksTitle}</h1>
        <p className="text-muted-foreground" data-testid="rx-thanks-line">{t.thanksBody(done)}</p>
        <Button
          variant="outline"
          className="btn-pill mt-8 h-11 px-6"
          onClick={() => setDone(null)}
          data-testid="rx-upload-another"
        >
          {t.another}
        </Button>
      </main>
    );
  }

  return (
    <main className="max-w-xl mx-auto px-5 sm:px-6 py-10 sm:py-14" data-testid="rx-upload-page">
      <div className="flex items-center justify-between gap-4 mb-8">
        <div className="overline">{t.overline}</div>
        {langToggle}
      </div>

      <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-4" data-testid="rx-title">{t.title}</h1>
      <p className="text-muted-foreground leading-relaxed mb-6">{t.subtitle}</p>

      <ol className="grid gap-2 mb-8">
        {t.steps.map((step, i) => (
          <li key={step} className="flex items-center gap-3 text-sm">
            <span className="w-6 h-6 rounded-full bg-accent text-primary text-xs flex items-center justify-center shrink-0">
              {i + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>

      {(ref || src) && (
        <div className="text-xs text-muted-foreground mb-6" data-testid="rx-referral-note">
          {t.referral((ref || src).toUpperCase())}
        </div>
      )}

      <form onSubmit={submit} className="space-y-5" data-testid="rx-form">
        <div>
          <label className="overline" htmlFor="rx-name">{t.name}</label>
          <Input
            id="rx-name"
            name="name"
            autoComplete="name"
            value={form.name}
            onChange={set("name")}
            placeholder={t.namePlaceholder}
            className="mt-2 h-12 bg-transparent"
            data-testid="rx-name"
          />
        </div>

        <div>
          <label className="overline" htmlFor="rx-phone">{t.phone}</label>
          <div className="relative mt-2">
            <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="rx-phone"
              name="tel"
              type="tel"
              autoComplete="tel-national"
              value={form.phone}
              onChange={set("phone")}
              inputMode="numeric"
              placeholder={t.phonePlaceholder}
              className="h-12 pl-9 bg-transparent"
              data-testid="rx-phone"
            />
          </div>
        </div>

        <div>
          <label className="overline" htmlFor="rx-address">{t.address}</label>
          <Textarea
            id="rx-address"
            name="street-address"
            autoComplete="street-address"
            value={form.address}
            onChange={set("address")}
            placeholder={t.addressPlaceholder}
            rows={3}
            className="mt-2 bg-transparent"
            data-testid="rx-address"
          />
          {known && (
            <p className="text-xs text-primary mt-2" data-testid="rx-known-note">{t.knownBack}</p>
          )}
        </div>

        <div>
          <label className="overline">{t.file}</label>
          <input
            ref={picker}
            type="file"
            accept="image/jpeg,image/png,image/*,application/pdf"
            onChange={pick}
            className="hidden"
            data-testid="rx-file-input"
          />
          <button
            type="button"
            onClick={() => picker.current?.click()}
            data-testid="rx-file-pick"
            className="mt-2 w-full rounded-xl border border-dashed border-border hover:border-primary/50 hover:bg-accent/40 transition-colors p-5 flex items-center gap-3 text-left"
          >
            <span className="w-10 h-10 rounded-lg bg-accent text-primary flex items-center justify-center shrink-0">
              {file ? <FileText size={18} /> : <Upload size={18} />}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium truncate" data-testid="rx-file-name">
                {file ? `${file.name} · ${readableSize(file.size)}` : t.choose}
              </span>
              <span className="block text-xs text-muted-foreground">{file ? t.change : t.fileHint}</span>
            </span>
          </button>
        </div>

        <div>
          <label className="overline" htmlFor="rx-note">{t.note}</label>
          <Textarea
            id="rx-note"
            name="note"
            autoComplete="off"
            value={form.note}
            onChange={set("note")}
            placeholder={t.notePlaceholder}
            rows={3}
            className="mt-2 bg-transparent"
            data-testid="rx-note"
          />
        </div>

        {/* Honeypot: hidden from people, irresistible to bots. */}
        <input
          type="text"
          name="company"
          value={form.company}
          onChange={set("company")}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] w-px h-px opacity-0"
          data-testid="rx-honeypot"
        />

        <label className="flex items-start gap-3 text-sm cursor-pointer">
          <Checkbox
            checked={form.consent}
            onCheckedChange={(v) => setForm((f) => ({ ...f, consent: !!v }))}
            className="mt-0.5"
            data-testid="rx-consent"
          />
          <span className="text-muted-foreground leading-relaxed">{t.consent}</span>
        </label>

        <Button type="submit" disabled={busy} className="btn-action w-full h-12" data-testid="rx-submit">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
          {busy ? t.sending : t.submit}
        </Button>

        <p className="text-xs text-muted-foreground flex items-center gap-2 justify-center">
          <Lock size={12} /> {t.privacy}
        </p>
      </form>
    </main>
  );
}
